// @ts-check

/**
 * @file Renders the timelog delete confirmation modal.
 */

import {memo} from "react"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {Pressable, Text, View} from "react-native"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import Modal from "../../../components/modal"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TimelogsDashboardDeleteConfirmModalProps
 * @property {string} bodyText Confirmation body copy.
 * @property {string} confirmLabel Confirm button label.
 * @property {boolean} deleting Whether deletion is in progress.
 * @property {string} deletingLabel Confirm button label while deleting.
 * @property {boolean} isVisible Whether the modal is visible.
 * @property {() => unknown} onCancelPress Cancel button callback.
 * @property {() => unknown} onConfirmPress Confirm button callback.
 * @property {string} title Modal title text.
 */

/** @typedef {Record<string, never>} TimelogsDashboardDeleteConfirmModalState */

/**
 * Timelogs dashboard delete confirmation modal.
 * @augments {ShapeComponent<TimelogsDashboardDeleteConfirmModalProps, TimelogsDashboardDeleteConfirmModalState>}
 */
class TimelogsDashboardDeleteConfirmModal extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  static propTypes = PropTypesExact({
    bodyText: PropTypes.string.isRequired,
    confirmLabel: PropTypes.string.isRequired,
    deleting: PropTypes.bool.isRequired,
    deletingLabel: PropTypes.string.isRequired,
    isVisible: PropTypes.bool.isRequired,
    onCancelPress: PropTypes.func.isRequired,
    onConfirmPress: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired
  })

  /**
   * Subscribes the modal to locale updates.
   * @returns {void} No return value.
   */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the confirmation modal shell.
   * @returns {ReactNode} Modal content.
   */
  render() {
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    return (
      <Modal
        cardStyle={styles[`card-${themeName}`] ||= {
          borderColor: colors.danger,
          gap: 12,
          maxWidth: 420,
          padding: 20
        }}
        isVisible={this.p.isVisible}
        onRequestClose={this.p.onCancelPress}
        testID="timelogsDashboardScreen/deleteConfirmModal"
      >
          <Text
            style={styles[`title-${themeName}`] ||= {
              color: colors.dangerText,
              fontSize: 20,
              fontWeight: 700
            }}
            testID="timelogsDashboardScreen/deleteConfirmModal/title"
          >
            {this.p.title}
          </Text>
          <Text
            style={styles[`body-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 15,
              lineHeight: 22
            }}
            testID="timelogsDashboardScreen/deleteConfirmModal/body"
          >
            {this.p.bodyText}
          </Text>
          <View
            style={styles.actions ||= {
              flexDirection: "row",
              gap: 10,
              justifyContent: "flex-end"
            }}
            testID="timelogsDashboardScreen/deleteConfirmModal/actions"
          >
            <Pressable
              onPress={this.p.onCancelPress}
              style={styles[`cancelButton-${themeName}`] ||= {
                alignItems: "center",
                backgroundColor: colors.surfaceMuted,
                borderColor: colors.border,
                borderRadius: 10,
                borderWidth: 1,
                flexDirection: "row",
                paddingHorizontal: 14,
                paddingVertical: 10
              }}
              testID="timelogsDashboardScreen/deleteConfirmModal/cancelButton"
            >
              <View
                style={styles.cancelButtonIcon ||= {
                  marginRight: 8,
                  width: 14
                }}
                testID="timelogsDashboardScreen/deleteConfirmModal/cancelButton/icon"
              >
                <FontAwesome6 color={colors.text} name="xmark" size={14} />
              </View>
              <Text
                style={styles[`cancelButtonText-${themeName}`] ||= {
                  color: colors.text,
                  fontSize: 14,
                  fontWeight: 600
                }}
                testID="timelogsDashboardScreen/deleteConfirmModal/cancelButton/text"
              >
                {_("Cancel")}
              </Text>
            </Pressable>
            <Pressable
              onPress={this.p.onConfirmPress}
              style={styles[`confirmButton-${themeName}`] ||= {
                alignItems: "center",
                backgroundColor: colors.danger,
                borderRadius: 10,
                flexDirection: "row",
                paddingHorizontal: 14,
                paddingVertical: 10
              }}
              testID="timelogsDashboardScreen/deleteConfirmModal/confirmButton"
            >
              <View
                style={styles.confirmButtonIcon ||= {
                  marginRight: 8,
                  width: 14
                }}
                testID="timelogsDashboardScreen/deleteConfirmModal/confirmButton/icon"
              >
                <FontAwesome6 color={colors.onPrimary} name="trash" size={14} />
              </View>
              <Text
                style={styles[`confirmButtonText-${themeName}`] ||= {
                  color: colors.onPrimary,
                  fontSize: 14,
                  fontWeight: 600
                }}
                testID="timelogsDashboardScreen/deleteConfirmModal/confirmButton/text"
              >
                {this.p.deleting ? this.p.deletingLabel : this.p.confirmLabel}
              </Text>
            </Pressable>
          </View>
      </Modal>
    )
  }
}

/** @type {NamedExoticComponent<TimelogsDashboardDeleteConfirmModalProps>} */
const TimelogsDashboardDeleteConfirmModalComponent = memo(shapeComponent(TimelogsDashboardDeleteConfirmModal))

export default TimelogsDashboardDeleteConfirmModalComponent
